import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import type { Message } from "@/types";

interface SendInput {
  body: string;
  senderRole: string;
}

export function useMessages(bookingId: number | null) {
  const { user, profile } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!bookingId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    try {
      const { data, error: fetchError } = await supabase
        .from("messages")
        .select("*")
        .eq("booking_id", bookingId)
        .order("created_at", { ascending: true });
      if (fetchError) throw fetchError;
      if (mounted.current) setMessages((data as Message[]) ?? []);
    } catch (err) {
      if (mounted.current) setError(err instanceof Error ? err.message : "We couldn't load this conversation.");
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    load();
  }, [load]);

  // Light polling keeps the thread fresh even if realtime isn't available.
  useEffect(() => {
    if (!bookingId) return undefined;
    const timer = window.setInterval(() => {
      load();
    }, 10000);
    return () => window.clearInterval(timer);
  }, [bookingId, load]);

  const send = useCallback(
    async ({ body, senderRole }: SendInput) => {
      const trimmed = body.trim();
      if (!bookingId || !trimmed) return;
      setSending(true);
      setError(null);
      try {
        const { data, error: insertError } = await supabase
          .from("messages")
          .insert({
            booking_id: bookingId,
            sender_id: user?.id ?? null,
            sender_name: profile?.full_name || user?.email || "Gari user",
            sender_role: senderRole,
            body: trimmed,
          })
          .select("*")
          .single();
        if (insertError) throw insertError;
        if (mounted.current) {
          setMessages((prev) => [...prev, data as Message]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Your message didn't send. Please try again.");
        throw err;
      } finally {
        setSending(false);
      }
    },
    [bookingId, user, profile]
  );

  return { messages, loading, error, sending, send, reload: load };
}